import { useContext, useRef, useEffect } from 'react';
import type React from 'react';
import classNames from 'classnames';
import uniqid from 'uniqid';
import { useQueryClient } from '@tanstack/react-query';

import Badge from 'components/partials/badge/badje';
import Loader from 'components/partials/loader/loader';
import Heading from 'components/partials/heading/heading';
import Tooltip from 'components/partials/tooltip/tooltip';
import MessageItem from './message-item';
import MessageInput, { type MessageInputHandle } from './message-input';
import MessageErrorItem from './message-error-item';
import NoMessagesContent from './no-messages-content';
import CohortsPopover from './cohorts-popover';

import { useCurrentUser } from 'state/current-user/current-user.thunk';
import { FeatureFlagsContext } from 'app';

import { useApiCreateConversation } from '../../hooks/use-api-create-conversation';
import { useApiGetConversationMessages } from '../../hooks/use-api-get-conversation-messages';
import { useApiCreateConversationMessage } from '../../hooks/use-api-create-conversation-message';
import { useApiRegenerateMessage } from '../../hooks/use-api-regenerate-message';
import { useAiAssistantSession } from '../../context/ai-assistant-session-context';
import useChatMessageInterceptor from '../../hooks/use-chat-message-interceptor';
import useActiveInputPart, {
  type ChatMessage,
} from '../../hooks/use-active-input-part';
import useInputPartLifecycle from '../../hooks/use-input-part-lifecycle';

import {
  resolveMessageParts,
  updateMessageById,
} from '../../helpers/message-helpers';

import { AI_ASSISTANT_CONVERSATIONS_KEY } from '../../ai-assistant.constants';

interface AiChatProps {
  isSidebarOpen: boolean;
  conversationId: string | null;
  setSearchParams: (params: Record<string, string>) => void;
  clientId: string | number;
  registerFocusMessageInput?: (focus: () => void) => void;
  isReadOnly?: boolean;
}

interface StreamChunk {
  stage?: string;
  parts?: Array<{ type: string; data?: unknown }>;
  content?: string;
  messageId?: string;
}

interface AiChatMessage extends ChatMessage {
  content?: string;
  loading?: boolean;
  error?: boolean;
  originalRequest?: string;
}

interface FeatureFlagsContextValue {
  featureFlags: Record<string, boolean>;
}

const AiChat = ({
  isSidebarOpen,
  conversationId,
  setSearchParams,
  clientId,
  registerFocusMessageInput,
  isReadOnly,
}: AiChatProps): React.JSX.Element => {
  const { featureFlags } = useContext(
    FeatureFlagsContext as unknown as React.Context<FeatureFlagsContextValue>
  );
  const isGuidedPromptEnabled = featureFlags['ai-guided-prompt'];
  const isBarChartEnabled = featureFlags['ai-bar-chart'];
  const shouldUseSendInterceptor = isGuidedPromptEnabled || isBarChartEnabled;
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const messageInputRef = useRef<MessageInputHandle>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const feedbackCloseRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    registerFocusMessageInput?.(() => messageInputRef.current?.focus());
  }, [registerFocusMessageInput]);

  const {
    getMessagesForConversation,
    setMessagesForConversation,
    getStreamingMessageId,
    setStreamingForConversation,
  } = useAiAssistantSession();

  const { data: conversationMessages = [], isFetching: isLoadingConversation } =
    useApiGetConversationMessages({
      oktaUserId: currentUser?.data?.oktaUserId as string,
      conversationId,
      clientId,
    });

  const messages = getMessagesForConversation(
    conversationId ?? ''
  ) as AiChatMessage[];

  useEffect(() => {
    const conversationMessagesArray = conversationMessages as AiChatMessage[];

    if (
      conversationId &&
      !isLoadingConversation &&
      (messages?.length ?? 0) === 0 &&
      conversationMessagesArray?.length > 0
    ) {
      setMessagesForConversation(
        conversationId,
        () => conversationMessagesArray
      );
    }
  }, [
    conversationId,
    isLoadingConversation,
    conversationMessages,
    messages?.length,
    setMessagesForConversation,
  ]);

  const { mutateAsync: createConversationMessage } =
    useApiCreateConversationMessage({
      oktaUserId: currentUser?.data?.oktaUserId as string,
      clientId,
    });

  const { mutateAsync: createConversation, isPending: isCreatingConversation } =
    useApiCreateConversation({
      oktaUserId: currentUser?.data?.oktaUserId as string,
      clientId,
    });

  const { mutateAsync: regenerateMessage } = useApiRegenerateMessage({
    oktaUserId: currentUser?.data?.oktaUserId as string,
    conversationId: conversationId ?? '',
    clientId,
  });

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (messages.length) scrollToBottom();
  }, [messages]);

  const activeInputPart = useActiveInputPart(messages);

  const { dismiss: dismissInputPart, submitAndSend: submitInputPart } =
    useInputPartLifecycle({
      activeMessage: activeInputPart?.message ?? null,
      conversationId,
      messages,
      setMessagesForConversation,
      sendMessage: (text: string) => {
        handleSendMessage(text);
      },
    });

  const { interceptSendMessage } = useChatMessageInterceptor({
    conversationId,
    isGuidedPromptEnabled,
    isBarChartEnabled,
    setMessagesForConversation,
    originalSendMessage: (requestMessage, isRetrying) => {
      void handleSendMessage(requestMessage, isRetrying);
    },
  });

  const createOnChunk =
    (targetConversationId: string, targetMessageId: string) =>
    (chunk: unknown): void => {
      const typedChunk = (chunk ?? {}) as StreamChunk;

      if (!chunk || Object.keys(typedChunk).length === 0) return;

      if (typedChunk.stage === 'completed') {
        const parts = resolveMessageParts(typedChunk);

        setStreamingForConversation(targetConversationId, null);
        setMessagesForConversation(targetConversationId, (prev) =>
          prev.map((message) =>
            message.id !== targetMessageId
              ? message
              : {
                  ...message,
                  id: typedChunk.messageId ?? message.id,
                  content: typedChunk.content ?? '',
                  parts,
                  loading: false,
                }
          )
        );

        return;
      }

      setMessagesForConversation(targetConversationId, (prev) =>
        prev.map((message) =>
          message.id !== targetMessageId
            ? message
            : { ...message, content: typedChunk.content, loading: true }
        )
      );
    };

  const setMessageError = (
    targetConversationId: string,
    messageId: string,
    { content, originalRequest }: { content: string; originalRequest?: string }
  ): void => {
    setStreamingForConversation(targetConversationId, null);
    setMessagesForConversation(targetConversationId, (previousMessages) =>
      updateMessageById(previousMessages, messageId, {
        content,
        error: true,
        loading: false,
        ...(originalRequest != null && { originalRequest }),
      })
    );
  };

  async function handleSendMessage(
    requestMessage: string | unknown,
    isRetrying = false
  ): Promise<void> {
    let activeConversationId = conversationId;
    const isFirstMessage = !messages.length;

    if (isFirstMessage) {
      await createConversation({
        clientId: currentUser?.data?.clientId,
      }).then((conversation) => {
        const typedConversation = conversation as { id: string };

        activeConversationId = typedConversation.id;
        setSearchParams({ conversationId: activeConversationId });
      });
    }

    const userMessage: AiChatMessage = {
      id: uniqid(),
      content: requestMessage as string,
      isUser: true,
    };

    const aiMessageId = uniqid();

    if (!isRetrying && activeConversationId) {
      setMessagesForConversation(activeConversationId, (prev) => [
        ...prev,
        userMessage,
      ]);
    }

    if (activeConversationId) {
      setMessagesForConversation(activeConversationId, (prev) => [
        ...prev,
        {
          id: aiMessageId,
          content: 'Generating response...',
          isUser: false,
          loading: true,
        } as AiChatMessage,
      ]);

      setStreamingForConversation(activeConversationId, aiMessageId);
    }

    try {
      if (activeConversationId) {
        await createConversationMessage({
          conversationId: activeConversationId,
          data: {
            requestMessage,
          },
          onChunk: createOnChunk(activeConversationId, aiMessageId),
        });
      }

      // Invalidate conversations to show the new conversation name in the sidebar
      if (isFirstMessage) {
        queryClient.invalidateQueries({
          queryKey: [AI_ASSISTANT_CONVERSATIONS_KEY],
        });
      }
    } catch {
      if (activeConversationId) {
        setMessageError(activeConversationId, aiMessageId, {
          content:
            'Something went wrong while generating your response. Please try again.',
          originalRequest: requestMessage as string,
        });
      }
    }
  }

  const handleRetry = async (
    messageId: string,
    originalRequest?: string
  ): Promise<void> => {
    if (!conversationId) return;
    setMessagesForConversation(conversationId, (prev) =>
      prev.filter((message) => message.id !== messageId)
    );
    await handleSendMessage(originalRequest, true);
  };

  const handleRegenerateMessage = async (messageId: string): Promise<void> => {
    const index = messages.findIndex((message) => message.id === messageId);

    if (index === -1 || !conversationId) return;

    setMessagesForConversation(conversationId, (prev) =>
      prev.slice(0, index + 1).map((message) =>
        message.id === messageId
          ? {
              ...message,
              content: 'Generating response...',
              loading: true,
            }
          : message
      )
    );

    setStreamingForConversation(conversationId, messageId);

    try {
      await regenerateMessage({
        messageId,
        onChunk: createOnChunk(conversationId, messageId),
      });
    } catch {
      setMessageError(conversationId, messageId, {
        content: 'Something went wrong while regenerating. Please try again.',
      });
    }
  };

  return (
    <div
      className={classNames(
        'relative flex flex-col w-full p-4 min-h-0 max-h-[calc(100vh-8rem)] transition-[margin-left] duration-300 ease-out',
        isSidebarOpen ? 'ml-64' : 'ml-0'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Heading
            variant="h3"
            customClass={classNames(
              'pl-2 py-2 flex-shrink-0 transition-[margin-left] duration-300 ease-out',
              isSidebarOpen ? 'ml-0' : 'ml-[104px]'
            )}
          >
            Ask MARK
          </Heading>
          <Tooltip
            position="bottom-start"
            content={
              <div>
                <p className="font-semibold">MARK is currently in Beta.</p>
                <p>
                  During this early-development stage, responses may be
                  incorrect. Please share your feedback to improve MARK's
                  accuracy.
                </p>
                <p>
                  Today, MARK analyzes Admissions, Financial Aid, and Prospect
                  View data only. Additional views and reporting are coming
                  soon. MARKETview does not use your data to train our model.
                </p>
              </div>
            }
            customClass="!bg-gray-700 !text-white !px-2 !py-1 max-w-80"
          >
            <Badge text="Beta" variant="reserve-outlined" />
          </Tooltip>
        </div>
        <CohortsPopover clientId={clientId} />
      </div>

      {isLoadingConversation && !messages.length ? (
        <Loader loading fullScreen={false} customClass="bg-white" />
      ) : (
        <div
          className={classNames('flex flex-col flex-1 min-h-0 gap-4 w-full', {
            'justify-between': messages.length,
            'justify-center': !messages.length,
          })}
        >
          {!isLoadingConversation && !messages.length && (
            <div className="max-w-[800px] mx-auto w-full">
              <NoMessagesContent
                onPropositionClick={
                  shouldUseSendInterceptor
                    ? interceptSendMessage
                    : handleSendMessage
                }
                isReadOnly={isReadOnly}
              />
            </div>
          )}

          {!!messages.length && (
            <div className="flex-1 min-h-0 overflow-y-auto mt-9 w-full scrollbar-thin">
              <div className="max-w-[800px] mx-auto w-full">
                {messages.map((message) =>
                  message.error ? (
                    <MessageErrorItem
                      key={message.id}
                      message={message}
                      onRetry={() =>
                        handleRetry(message.id, message.originalRequest)
                      }
                    />
                  ) : (
                    <MessageItem
                      key={`${message.id}-${Boolean(message.loading)}`}
                      message={message}
                      oktaUserId={currentUser?.data?.oktaUserId as string}
                      clientId={clientId}
                      conversationId={conversationId ?? ''}
                      feedbackCloseRef={feedbackCloseRef}
                      onRegenerateMessage={handleRegenerateMessage}
                      isReadOnly={isReadOnly}
                    />
                  )
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}

          <div className="max-w-[800px] mx-auto w-full">
            {isGuidedPromptEnabled && activeInputPart ? (
              <activeInputPart.Component
                data={activeInputPart.data}
                onSubmit={submitInputPart}
                onDismiss={dismissInputPart}
              />
            ) : (
              <MessageInput
                ref={messageInputRef}
                onSendMessage={
                  shouldUseSendInterceptor
                    ? (interceptSendMessage as (m: string) => void)
                    : (handleSendMessage as (m: string) => void)
                }
                disabled={
                  isLoadingConversation ||
                  isCreatingConversation ||
                  getStreamingMessageId(conversationId ?? '') != null ||
                  isReadOnly
                }
              />
            )}
            <p className="text-sm text-gray-500 text-center mt-1">
              MARK is currently in Beta and responses may be incorrect.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiChat;
